<template>
  <b-container fluid class="py-3 px-3 px-md-4">
  <div class="d-flex flex-wrap justify-content-between align-items-center mb-4" style="gap: 0.5rem;">
    <h1 class="fs-22 font-weight-bold mb-0" style="color: #163D7D; line-height: 1">Gestión de planos</h1>

    <b-dropdown
      variant="light"
      no-caret
      right
      size="sm"
      toggle-class="rounded-pill border fs-13 text-info px-3"
    >
      <template #button-content>
        <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
        <span class="ml-0">¿Cómo crear una solicitud?</span>
      </template>

      <b-dropdown-text style="width: 300px">
        <p class="fs-14 mb-2 text-dark">
          Las solicitudes se crean desde el <strong>proyecto del cliente</strong> y deben incluir al menos un submital
        </p>
        <ol class="fs-14 text-muted pl-3 mb-0">
          <li>Ingrese al proyecto del cliente</li>
          <li>Verifique que tenga al menos un submital</li>
          <li>Cree la solicitud desde la sección de planos</li>
        </ol>
      </b-dropdown-text>

      <b-dropdown-divider />

      <b-dropdown-item to="/app/prod/proyectos" link-class="fs-14 font-weight-bold text-primary">
        Ir a proyectos
      </b-dropdown-item>
    </b-dropdown>
  </div>

    <!-- Search bar and filters -->
    <b-row class="mt-2">
      <b-col md="6">
        <div class="d-flex align-items-center">
          <b-form-group style="min-width: 88%" class="mb-0">
            <b-input-group>
              <b-input
                v-model="searchTerm"
                autofocus
                placeholder="Buscar por número o cliente"
                @keyup.enter="search"
              />
              <b-input-group-append class="mb-0">
                <b-button variant="outline-secondary" @click="search">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-form-group class="ml-3 mb-0">
            <b-button variant="outline-secondary" @click="resetSearch">
              <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
            </b-button>
          </b-form-group>
        </div>
      </b-col>
      <b-col md="6" align-self="end">
        <div class="d-flex justify-content-end mt-2 mt-md-0">
          <b-button
            v-if="activeFilters.length > 0"
            variant="outline-secondary"
            class="mr-2"
            @click="resetFilters"
          >
            <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
          </b-button>
          <b-dropdown dropleft variant="primary" no-caret>
            <template #button-content>
              <div class="d-flex align-items-center">
                <font-awesome-icon :icon="['far', 'filter']" fixed-width />
                <span class="ml-2 mr-2">Filtros</span>
                <div class="filter-count">
                  <span>{{ activeFilters.length }}</span>
                </div>
              </div>
            </template>
            <b-dropdown-form style="width: 19rem">
              <b-form-group>
                <template #label><span>Estado</span></template>
                <b-select
                  :value="filters.status_filter"
                  :options="statusFilterOptions"
                  @input="updateFilter('status_filter', $event)"
                >
                  <template #first>
                    <option :value="null">-- Seleccione --</option>
                  </template>
                </b-select>
              </b-form-group>

              <b-form-group v-if="isDrawingAdmin">
                <template #label><span>Dibujante</span></template>
                <b-select
                  :value="filters.drafter_id"
                  :options="drafterOptions"
                  :disabled="loadingDrafters"
                  @input="updateFilter('drafter_id', $event)"
                >
                  <template #first>
                    <option :value="null">{{ loadingDrafters ? 'Cargando...' : '-- Seleccione --' }}</option>
                    <option :value="NO_DRAFTER">Sin definir</option>
                  </template>
                </b-select>
              </b-form-group>
            </b-dropdown-form>
          </b-dropdown>
        </div>
      </b-col>
    </b-row>

    <div class="d-flex flex-wrap justify-content-between align-items-center mt-2">
      <div class="view-switch d-inline-flex flex-wrap mb-2 mr-2" role="group" aria-label="Vistas">
        <button
          v-for="view in views"
          :key="view.value"
          type="button"
          class="view-switch-item"
          :class="{ active: viewFilter === view.value }"
          :disabled="loading"
          @click="changeView(view.value)"
        >
          {{ view.text }}
        </button>
      </div>

      <div class="d-flex align-items-center mb-2">
        <span class="fs-15 text-muted mr-3">Resultados: {{ count }}</span>
        <div class="view-switch d-inline-flex" role="group" aria-label="Abiertas o cerradas">
          <button
            v-for="option in openStatusOptions"
            :key="option.text"
            type="button"
            class="view-switch-item"
            :class="{ active: openStatusFilter === option.value }"
            :disabled="loading"
            @click="changeOpenStatus(option.value)"
          >
            {{ option.text }}
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <b-row class="d-none d-xl-block">
      <b-col>
        <b-table
          :items="loading ? [] : drawingRequests"
          :fields="fields"
          class="fs-15"
          show-empty
          hover
          responsive
          no-local-sorting
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @sort-changed="onSortChanged"
          @row-clicked="openRequest"
        >
          <template #empty>
            <div v-if="loading" class="text-center text-secondary">
              <b-spinner small type="grow" />
              <span class="pl-2 fs-18">Cargando solicitudes</span>
            </div>
            <div v-else class="text-center font-italic">
              <span v-if="hasSearch" class="text-danger fs-18">No hay resultados</span>
              <span v-else class="text-secondary fs-18">No hay solicitudes</span>
            </div>
          </template>

          <template #cell(request_no)="{ item }">
            <b-link :to="showRoute(item)" class="font-weight-bold text-nowrap">{{ item.request_no }}</b-link>
          </template>

          <template #cell(client_name)="{ item }">
            <div class="d-flex flex-column" style="line-height: 19px">
              <p class="mb-0 font-weight-bold" style="color: #333">{{ item.client_num }}</p>
              <p class="mb-0 text-muted fs-15 text-uppercase">{{ item.client_name }}</p>
            </div>
          </template>

          <template #cell(requester)="{ value }">
            <span v-if="value">{{ value | concatName2 | titleize }}</span>
          </template>

          <template #cell(drafter)="{ value }">
            <span v-if="value">{{ value | concatName2 | titleize }}</span>
            <span v-else class="text-muted">Sin asignar</span>
          </template>

          <template #cell(approver)="{ value }">
            <span v-if="value">{{ value | concatName2 | titleize }}</span>
            <span v-else class="text-muted">Sin asignar</span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge pill :variant="statusVariant(item.status)" class="fs-12">{{ item.status_label }}</b-badge>
          </template>

          <template #cell(required_date)="{ item }">
            <span class="due-date font-weight-bold text-nowrap" :class="dueClass(item)">{{ item.required_date | formatDate }}</span>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mt-2 d-xl-none">
      <b-col
        v-for="item in loading ? [] : drawingRequests"
        :key="item.id"
        cols="12"
        class="mb-3"
      >
        <b-card
          class="shadow-sm"
          :class="dueClass(item)"
          style="cursor: pointer; border-radius: 10px"
          @click="openRequest(item)"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex flex-column" style="line-height: 19px; min-width: 0">
              <b-link :to="showRoute(item)" class="font-weight-bold" @click.native.stop>{{ item.request_no }}</b-link>
              <p class="mb-0 font-weight-bold mt-1" style="color: #333">{{ item.client_num }}</p>
              <p class="mb-0 text-muted fs-15 text-uppercase text-clamp-2">{{ item.client_name }}</p>
            </div>
            <b-badge pill :variant="statusVariant(item.status)" class="fs-12 ml-2 flex-shrink-0">{{ item.status_label }}</b-badge>
          </div>
          <div class="d-flex justify-content-between fs-14 text-muted mt-2">
            <span class="text-truncate mr-2" style="min-width: 0">
              {{ item.drawing_type_label }} · {{ userName(item.drafter) }}
            </span>
            <span class="font-weight-bold text-dark flex-shrink-0">{{ item.required_date | formatDate }}</span>
          </div>
        </b-card>
      </b-col>

      <b-col v-if="!drawingRequests.length && !loading">
        <p class="text-center text-muted font-italic">
          <span v-if="hasSearch" class="text-danger fs-18">No hay resultados</span>
          <span v-else class="fs-18">No hay solicitudes</span>
        </p>
      </b-col>
      <b-col v-if="loading">
        <div class="text-center text-secondary">
          <b-spinner small type="grow" />
          <span class="pl-2 fs-18">Cargando solicitudes</span>
        </div>
      </b-col>
    </b-row>

    <!-- Pagination -->
    <b-row>
      <b-col>
        <div class="d-flex justify-content-center my-3">
          <PaginationCountless :page="page" :next="next" :prev="prev">
            <template #prev-label>
              <font-awesome-icon :icon="['fas', 'backward']" size="lg" fixed-width />
            </template>
            <template #next-label>
              <font-awesome-icon :icon="['fas', 'forward']" size="lg" fixed-width />
            </template>
          </PaginationCountless>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import PaginationCountless from '../../shared/PaginationCountless'
import { statusOptions, statusVariants, closedStatuses } from '../../../sharedOptions/drawingRequestOptions'

const NO_DRAFTER = 'none'

const FIELD_CLASSES = { tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato' }

const FIELDS = [
  { key: 'request_no', label: 'Solicitud', sortable: true, ...FIELD_CLASSES },
  { key: 'client_name', label: 'Cliente', sortable: true, ...FIELD_CLASSES },
  { key: 'drawing_type_label', label: 'Tipo de plano', sortable: true, ...FIELD_CLASSES },
  { key: 'requester', label: 'Solicitante', sortable: true, ...FIELD_CLASSES },
  { key: 'drafter', label: 'Dibujante', sortable: true, ...FIELD_CLASSES },
  { key: 'approver', label: 'Aprobador', sortable: true, ...FIELD_CLASSES },
  { key: 'status', label: 'Estado', sortable: true, ...FIELD_CLASSES },
  { key: 'required_date', label: 'Fecha requerida', sortable: true, ...FIELD_CLASSES }
]

const HIDDEN_FIELD_BY_VIEW = {
  requested: 'requester',
  drafting: 'drafter',
  approving: 'approver'
}

const DUE_VARIANTS = {
  overdue: 'danger',
  today: 'warning',
  onTime: null
}

export default {
  name: 'DrawingRequestIndex',
  components: { PaginationCountless },
  data () {
    return {
      drawingRequests: [],
      loading: false,
      drafters: [],
      loadingDrafters: false,
      page: 1,
      next: null,
      prev: null,
      count: 0,
      NO_DRAFTER,
      openStatusOptions: [
        { value: true, text: 'Abiertas' },
        { value: false, text: 'Cerradas' }
      ]
    }
  },
  computed: {
    ...mapGetters(['isDrawingDrafter', 'isDrawingAdmin']),
    ...mapState('drawingRequests', ['filters']),
    views () {
      return [
        { value: 'requested', text: 'Mis solicitudes', show: true },
        { value: 'drafting', text: 'Dibujante', show: this.isDrawingDrafter },
        { value: 'approving', text: 'Aprobador', show: true },
        { value: 'all', text: 'Todas', show: this.isDrawingAdmin }
      ].filter(view => view.show)
    },
    fields () {
      const hiddenField = HIDDEN_FIELD_BY_VIEW[this.viewFilter]

      return FIELDS.filter(field => field.key !== hiddenField)
    },
    viewFilter: {
      get () { return this.$store.state.drawingRequests.viewFilter },
      set (value) { this.$store.commit('drawingRequests/setViewFilter', value) }
    },
    openStatusFilter: {
      get () { return this.$store.state.drawingRequests.openStatusFilter },
      set (value) { this.$store.commit('drawingRequests/setOpenStatusFilter', value) }
    },
    searchTerm: {
      get () { return this.$store.state.drawingRequests.searchTerm },
      set (value) { this.$store.commit('drawingRequests/setSearchTerm', value) }
    },
    sortBy: {
      get () { return this.$store.state.drawingRequests.sortBy },
      set (value) { this.$store.commit('drawingRequests/setSortBy', value) }
    },
    sortDesc: {
      get () { return this.$store.state.drawingRequests.sortDesc },
      set (value) { this.$store.commit('drawingRequests/setSortDesc', value) }
    },
    statusFilterOptions () {
      return statusOptions.filter(option => closedStatuses.includes(option.value) !== this.openStatusFilter)
    },
    drafterOptions () {
      return this.drafters.map(user => ({ value: user.id, text: this.userName(user) }))
    },
    activeFilters () {
      const filters = []
      if (this.filters.status_filter) {
        const option = statusOptions.find(o => o.value === this.filters.status_filter)
        filters.push({ key: 'status_filter', text: option ? option.text : this.filters.status_filter })
      }
      if (this.filters.drafter_id && this.isDrawingAdmin) {
        filters.push({ key: 'drafter_id', text: this.drafterFilterText })
      }
      return filters
    },
    drafterFilterText () {
      if (this.filters.drafter_id === NO_DRAFTER) return 'Sin dibujante'
      const option = this.drafterOptions.find(o => o.value === this.filters.drafter_id)
      return option ? option.text : 'Dibujante'
    },
    hasSearch () {
      return !!this.searchTerm || this.activeFilters.length > 0
    },
    today () {
      const now = new Date()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      return `${now.getFullYear()}-${month}-${day}`
    }
  },
  watch: {
    '$route.query' () {
      this.fetchDrawingRequests()
    }
  },
  mounted () {
    if (!this.views.some(view => view.value === this.viewFilter)) this.viewFilter = 'requested'
    if (this.isDrawingAdmin) this.fetchDrafters()
    this.fetchDrawingRequests()
  },
  methods: {
    fetchDrawingRequests () {
      if (this.loading) return
      this.loading = true

      this.$http.get('/api/production/drawing_requests', {
        params: {
          page: this.$route.query.pagina,
          view_filter: this.viewFilter,
          open_status_filter: String(this.openStatusFilter),
          q: this.searchTerm,
          sort_by: this.sortBy,
          sort_desc: String(this.sortDesc),
          status_filter: this.filters.status_filter,
          drafter_id: this.isDrawingAdmin ? this.filters.drafter_id : null
        }
      })
        .then((res) => {
          this.drawingRequests = res.data.drawing_requests
          this.setPagination(res.data.pagy)
        })
        .catch((error) => {
          this.drawingRequests = []
          console.log(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setPagination (pagy) {
      this.page = pagy.page
      this.count = pagy.count
      this.next = pagy.page < pagy.last ? pagy.page + 1 : null
      this.prev = pagy.page > 1 ? pagy.page - 1 : null
    },
    search () {
      const query = { ...this.$route.query }
      if (query.pagina) {
        delete query.pagina
        this.$router.replace({ query }).catch(() => {})
      } else {
        this.fetchDrawingRequests()
      }
    },
    resetSearch () {
      this.searchTerm = ''
      this.search()
    },
    fetchDrafters () {
      if (this.drafters.length || this.loadingDrafters) return
      this.loadingDrafters = true

      this.$http.get('/api/production/drawing_requests/drafters')
        .then((res) => {
          this.drafters = res.data
        })
        .catch(e => console.log(e))
        .finally(() => {
          this.loadingDrafters = false
        })
    },
    updateFilter (key, value) {
      this.$store.commit('drawingRequests/setFilter', { key, value })
      this.search()
    },
    resetFilters () {
      this.$store.commit('drawingRequests/resetFilters')
      this.search()
    },
    changeView (view) {
      if (view === this.viewFilter) return
      this.viewFilter = view
      this.search()
    },
    changeOpenStatus (value) {
      if (value === this.openStatusFilter) return
      this.openStatusFilter = value
      this.search()
    },
    onSortChanged (ctx) {
      if (!ctx.sortBy) return
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.search()
    },
    showRoute (item) {
      // TODO: confirmar el nombre de la ruta del show
      return { name: 'DrawingRequestShow', params: { id: item.id } }
    },
    openRequest (item) {
      this.$router.push(this.showRoute(item))
    },
    userName (user) {
      if (!user) return 'Sin asignar'
      return `${user.first_name} ${user.last_name || ''}`.trim()
    },
    statusVariant (status) {
      return statusVariants[status] || 'secondary'
    },
    dueVariant (item) {
      if (!item.required_date || closedStatuses.includes(item.status)) return null
      if (item.required_date < this.today) return DUE_VARIANTS.overdue
      if (item.required_date === this.today) return DUE_VARIANTS.today
      return DUE_VARIANTS.onTime
    },
    dueClass (item) {
      const variant = this.dueVariant(item)
      return variant ? `due-${variant}` : null
    }
  }
}
</script>

<style scoped>
.view-switch {
  background: #e9edf3;
  border-radius: 8px;
  padding: 3px;
}
.view-switch-item {
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #5f6873;
  font-size: 15px;
  padding: 5px 14px;
}
.view-switch-item.active {
  background: #ffffff;
  color: #163D7D;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  height: 1.4rem;
  padding: 0 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #007bff;
  background-color: #fff;
  border-radius: 1rem;
}
.due-date.due-danger {
  color: #c82333;
}
.due-date.due-warning {
  color: #b07d00;
}
.card.due-danger {
  background-color: #f8d7da;
}
.card.due-warning {
  background-color: #fff3cd;
}
</style>
